"use client"

import { useEffect, useRef } from "react"

/**
 * 画面の自動スリープを防止するhook（Screen Wake Lock API）。
 *
 * - enabled=true の間だけスリープ防止を有効にする（出勤ON中に使用）。
 * - Wake Lockはタブ/アプリが非表示になると自動解放されるため、復帰時に再取得する。
 * - 非対応ブラウザ（古いiOS等）や取得失敗（バッテリーセーバー等）は無視する。
 *
 * 対応: Chrome/Android、iOS Safari 16.4+。
 */
export function useWakeLock(enabled: boolean) {
  const sentinelRef = useRef<WakeLockSentinel | null>(null)

  useEffect(() => {
    if (!enabled) return
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return

    let cancelled = false

    const requestWakeLock = async () => {
      try {
        const sentinel = await navigator.wakeLock.request("screen")
        if (cancelled) {
          // クリーンアップ後に解決した場合はすぐ解放する
          sentinel.release().catch(() => {})
          return
        }
        sentinelRef.current = sentinel
        // 自動解放（タブ非表示等）されたら参照をクリアし、再表示時の再取得を可能にする
        sentinel.addEventListener("release", () => {
          if (sentinelRef.current === sentinel) {
            sentinelRef.current = null
          }
        })
      } catch {
        // 非対応・権限なし・バッテリーセーバー等は無視（次の可視化で再試行）
      }
    }

    // 非表示中に自動解放されたWake Lockを、再表示時に取得し直す
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !cancelled && sentinelRef.current === null) {
        requestWakeLock()
      }
    }

    requestWakeLock()
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      cancelled = true
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      sentinelRef.current?.release().catch(() => {})
      sentinelRef.current = null
    }
  }, [enabled])
}
